<template>
  <div class="pay-fee-qrcode-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('payFeeQrcode.list.title')" :subtitle="$t('payFeeQrcode.page.subtitle')">
      <template #extra>
        <el-button type="primary" icon="el-icon-plus" @click="handleAdd">
          {{ $t('common.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.qrcodeName" clearable class="w-full"
            :placeholder="$t('payFeeQrcode.search.qrcodeName')" @keyup.enter.native="handleSearch" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('payFeeQrcode.list.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('payFeeQrcode.page.empty')" @retry="getList">
        <el-table-column prop="qrcodeName" :label="$t('payFeeQrcode.table.qrcodeName')" align="left" min-width="150"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.qrcodeName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="queryWay" :label="$t('payFeeQrcode.table.queryWay')" align="center" width="170">
          <template slot-scope="scope">
            <span v-if="scope.row.queryWay === '1001'">
              {{ $t('payFeeQrcode.queryWay.byPhone') }}
            </span>
            <span v-else-if="scope.row.queryWay === '2002'">
              {{ $t('payFeeQrcode.queryWay.byHouse') }}
            </span>
            <span v-else>
              {{ $t('payFeeQrcode.queryWay.byBoth') }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="smsValidate" :label="$t('payFeeQrcode.table.smsValidate')" align="center" width="100">
          <template slot-scope="scope">
            <ds-status-tag :type="scope.row.smsValidate === 'ON' ? 'success' : 'muted'"
              :label="scope.row.smsValidate === 'ON' ? $t('common.yes') : $t('common.no')" />
          </template>
        </el-table-column>
        <el-table-column prop="customFee" :label="$t('payFeeQrcode.table.customFee')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag :type="scope.row.customFee === 'ON' ? 'success' : 'muted'"
              :label="scope.row.customFee === 'ON' ? $t('common.yes') : $t('common.no')" />
          </template>
        </el-table-column>
        <el-table-column prop="feeType" :label="$t('payFeeQrcode.table.feeType')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag type="info"
              :label="scope.row.feeType === 'OWNER' ? $t('payFeeQrcode.feeType.owner') : $t('payFeeQrcode.feeType.house')" />
          </template>
        </el-table-column>
        <el-table-column prop="state" :label="$t('payFeeQrcode.table.state')" align="center" width="100">
          <template slot-scope="scope">
            <ds-status-tag :type="scope.row.state === 'ON' ? 'success' : 'muted'"
              :label="scope.row.state === 'ON' ? $t('common.enabled') : $t('common.disabled')" />
          </template>
        </el-table-column>
        <el-table-column prop="createStaffName" :label="$t('payFeeQrcode.table.createStaffName')" align="center" width="120">
          <template slot-scope="scope">
            {{ scope.row.createStaffName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('payFeeQrcode.table.createTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatDateTimeSec(scope.row.createTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" fixed="right" width="200">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="handleView(scope.row)">
              {{ $t('payFeeQrcode.operation.viewQrcode') }}
            </el-button>
            <el-button size="mini" type="text" @click="handleEdit(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="handleDelete(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('payFeeQrcode.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <!-- Components -->
    <add-pay-fee-qrcode ref="addDialog" @success="handleSuccess" />
    <edit-pay-fee-qrcode ref="editDialog" @success="handleSuccess" />
    <delete-pay-fee-qrcode ref="deleteDialog" @success="handleSuccess" />
    <view-pay-fee-qrcode ref="viewDialog" />
  </div>
</template>

<script>
import { listPayFeeQrcode } from '@/api/fee/payFeeQrcodeApi'
import AddPayFeeQrcode from '@/components/fee/addPayFeeQrcode'
import EditPayFeeQrcode from '@/components/fee/editPayFeeQrcode'
import DeletePayFeeQrcode from '@/components/fee/deletePayFeeQrcode'
import ViewPayFeeQrcode from '@/components/fee/viewPayFeeQrcode'
import { getCommunityId } from '@/api/community/communityApi'
import { formatDateTimeSec } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'PayFeeQrcodeList',
  components: {
    AddPayFeeQrcode,
    EditPayFeeQrcode,
    DeletePayFeeQrcode,
    ViewPayFeeQrcode,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        qrcodeName: '',
        customFee: '',
        preFee: ''
      },
      tableData: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      communityId: ''
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.getList()
  },
  methods: {
    async getList() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: this.pagination.current,
          row: this.pagination.size,
          qrcodeName: this.searchForm.qrcodeName,
          communityId: this.communityId
        }
        const { data, total } = await listPayFeeQrcode(params)
        this.tableData = Array.isArray(data) ? data : []
        this.pagination.total = total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('payFeeQrcode.page.loadFailed')
        this.$message.error(this.$t('payFeeQrcode.message.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm = {
        qrcodeName: '',
        customFee: '',
        preFee: ''
      }
      this.handleSearch()
    },
    handleAdd() {
      this.$refs.addDialog.open()
    },
    handleEdit(row) {
      this.$refs.editDialog.open(row)
    },
    handleDelete(row) {
      this.$refs.deleteDialog.open(row)
    },
    handleView(row) {
      this.$refs.viewDialog.open(row)
    },
    handleSuccess() {
      this.getList()
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.getList()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this.getList()
    },
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.pay-fee-qrcode-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
